import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import * as React from 'react';
import { useBoxSize, useThemeValue } from '../system';
import Icon from '../Icon';
import Link from '../Link';
import Spinner from '../Spinner';
import { StackView } from '../StackView';
import Tooltip from '../Tooltip';
import { mergeProps } from '../utils';
import { useAccessibilityViolation } from '../hooks/use-accessibility-violation';
import Input from './Input';

var _ref2 = /*#__PURE__*/React.createElement(Link, {
  external: true,
  to: "https://planningcenter.github.io/tapestry-react/button#tooltip",
  weight: 700
}, "tooltip");

var _ref3 = /*#__PURE__*/React.createElement(Link, {
  external: true,
  to: "https://planningcenter.github.io/tapestry-react/button#title",
  weight: 700
}, "title");

export function Button(_ref) {
  var children = _ref.children,
      disabled = _ref.disabled,
      external = _ref.external,
      icon = _ref.icon,
      iconLeft = _ref.iconLeft,
      iconRight = _ref.iconRight,
      onClick = _ref.onClick,
      onKeyDown = _ref.onKeyDown,
      size = _ref.size,
      spinner = _ref.spinner,
      square = _ref.square,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'default' : _ref$theme,
      title = _ref.title,
      to = _ref.to,
      tooltip = _ref.tooltip,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'button' : _ref$type,
      variant = _ref.variant,
      restProps = _objectWithoutPropertiesLoose(_ref, ["children", "disabled", "external", "icon", "iconLeft", "iconRight", "onClick", "onKeyDown", "size", "spinner", "square", "theme", "title", "to", "tooltip", "type", "variant"]);

  var buttonThemes = useThemeValue('button.themes');

  var _useBoxSize = useBoxSize(size),
      boxSize = _useBoxSize.boxSize,
      fontSize = _useBoxSize.fontSize,
      lineHeight = _useBoxSize.lineHeight,
      paddingHorizontal = _useBoxSize.paddingHorizontal,
      paddingHorizontalDense = _useBoxSize.paddingHorizontalDense,
      paddingVertical = _useBoxSize.paddingVertical;

  var buttonProps = {
    alignment: 'center',
    as: restProps.href || to ? 'a' : 'button',
    axis: 'horizontal',
    distribution: 'center',
    fontSize: fontSize,
    fontWeight: 700,
    inline: true,
    lineHeight: lineHeight,
    position: 'relative',
    radius: 3,
    strokeAlign: 'inside',
    strokeWeight: 1,
    userSelect: 'none',
    zIndex: 1,
    type: type
  };
  var spinnerColor;

  if (theme) {
    var buttonTheme = buttonThemes[theme];

    if (buttonTheme) {
      var buttonThemeVariant = buttonTheme[variant || 'fill'];

      if (buttonThemeVariant) {
        buttonProps = _objectSpread(_objectSpread({}, buttonProps), buttonThemeVariant);
      } else {
        console.warn("Could not find \"" + variant + "\" in \"" + theme + "\" theme. Check your ThemeProvider and make sure \"" + variant + "\" variant exists in \"" + theme + "\".");
      }
    } else if (theme === 'false') {
      console.warn("The string \"false\" is not a valid theme. Please pass a boolean %cfalse%c instead.", 'color:red', 'color:inherit');
    } else {
      console.warn("Could not find \"" + theme + "\" in button.themes. Check your ThemeProvider and make sure \"" + theme + "\" exists.");
    }
  } // normalize strings that should end up as objects


  if (typeof icon === 'string') {
    icon = {
      name: icon
    };
  }

  if (typeof iconLeft === 'string') {
    iconLeft = {
      name: iconLeft
    };
  }

  if (typeof iconRight === 'string') {
    iconRight = {
      name: iconRight
    };
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      title: tooltip
    };
  } // if using an icon only button, apply dense padding and possibly an aria label


  if (icon) {
    paddingHorizontal = paddingHorizontalDense;
    buttonProps['aria-label'] = tooltip ? tooltip['title'] : title;
  } // square width and height if applicable props present


  if (square) {
    buttonProps.width = boxSize;
    buttonProps.height = boxSize;
  } else if (icon) {
    buttonProps.minWidth = boxSize;
    buttonProps.minHeight = boxSize;
  } // handle padding based on the incoming radius or whether icons are present or not


  if (restProps['radius'] === 'pill') {
    buttonProps.paddingHorizontal = paddingHorizontal * 1.25;
  } else if (restProps['radius'] === 'pill-left') {
    buttonProps.paddingLeft = paddingHorizontal * 1.25;
    buttonProps.paddingRight = paddingHorizontal;
  } else if (restProps['radius'] === 'pill-right') {
    buttonProps.paddingLeft = paddingHorizontal;
    buttonProps.paddingRight = paddingHorizontal * 1.25;
  } else if (!(icon && !iconLeft && !iconRight)) {
    buttonProps.paddingHorizontal = paddingHorizontal;
    buttonProps.paddingVertical = paddingVertical;
  } // clear text and/or icons when spinner is present


  if (spinner) {
    spinnerColor = buttonProps.color;
    buttonProps.color = 'transparent';

    if (icon) {
      icon['color'] = 'transparent';
    }

    if (iconLeft) {
      iconLeft['color'] = 'transparent';
    }

    if (iconRight) {
      iconRight['color'] = 'transparent';
    }
  }

  if (disabled) {
    var themeData = buttonThemes[theme];

    if (themeData) {
      var variantData = themeData[variant];

      if (variantData && variantData.disabled) {
        buttonProps = _objectSpread(_objectSpread({}, buttonProps), variantData.disabled);
      } else {
        buttonProps.opacity = 0.65;
      }
    }

    buttonProps['aria-disabled'] = true;
    buttonProps.cursor = 'not-allowed';

    buttonProps.onClick = function (event) {
      return event.preventDefault();
    };

    buttonProps.onKeyDown = function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
      }
    };
  } // don't apply hover/active styles or events when disabled or spinner is present


  if (disabled || spinner) {
    buttonProps.hover = undefined;
    buttonProps.active = undefined;
  } else {
    buttonProps.focus = _objectSpread({
      zIndex: 5
    }, buttonProps.focus);
    buttonProps.cursor = 'pointer';
    buttonProps.touchAction = 'manipulation';
    buttonProps.onClick = onClick;

    buttonProps.onKeyDown = function (event) {
      if (onClick && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick(event);
      }

      if (onKeyDown) {
        onKeyDown(event);
      }
    };
  } // apply rel and target for external links


  if (external) {
    buttonProps.rel = 'noopener noreferrer';
    buttonProps.target = '_blank';
  } // apply href value when specified


  if (to) {
    restProps['href'] = to;
  } // remove `type` if either `to` or `href` is specified


  if (to || restProps.href) {
    buttonProps = _objectSpread(_objectSpread({}, buttonProps), {}, {
      type: null
    });
  } // apply stroke defaults and higher z-index when hovering to show outline in group properly


  if (variant === 'outline') {
    buttonProps = _objectSpread(_objectSpread({}, buttonProps), {}, {
      hover: _objectSpread({
        strokeAlign: 'inside',
        strokeWeight: 1,
        zIndex: 2
      }, buttonProps.hover)
    });
  } // pass to if as is defined


  if (restProps.as) {
    buttonProps.to = to; // remove type if it is not a button

    if (restProps.as !== 'button') {
      buttonProps = _objectSpread(_objectSpread({}, buttonProps), {}, {
        type: null
      });
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    if (icon && !(title || tooltip && tooltip.title)) {
      var _useAccessibilityViol = useAccessibilityViolation( /*#__PURE__*/React.createElement(React.Fragment, null, "Icon only buttons must define either a", ' ', _ref2, ' ', "or", ' ', _ref3, ' ', "prop."), "Tapestry-React: <Button/> icon only buttons must define either a \"tooltip\" or \"title\" prop for proper accessibility."),
          tooltipProps = _useAccessibilityViol.tooltipProps,
          wrapperProps = _useAccessibilityViol.wrapperProps;

      tooltip = tooltipProps;
      restProps = mergeProps(restProps, wrapperProps);
    }

    if (buttonProps.type && restProps.as && restProps.as !== 'button') {
      console.log("Tapestry-React: <Button/> type prop is only supported by <button> and not <" + restProps.as + ">.");
    }
  }

  return /*#__PURE__*/React.createElement(Tooltip, tooltip, /*#__PURE__*/React.createElement(StackView, _extends({}, buttonProps, restProps, {
    css: {
      '&:focus-visible': _objectSpread({}, buttonProps.focusVisible)
    }
  }), iconLeft && /*#__PURE__*/React.createElement(Icon, _extends({
    size: size,
    color: buttonProps.color,
    marginRight: paddingHorizontalDense
  }, iconLeft)), icon ? /*#__PURE__*/React.createElement(Icon, _extends({
    size: size,
    color: buttonProps.color
  }, icon)) : title || children, iconRight && /*#__PURE__*/React.createElement(Icon, _extends({
    size: size,
    color: buttonProps.color,
    marginLeft: paddingHorizontalDense
  }, iconRight)), spinner && /*#__PURE__*/React.createElement(Spinner, _extends({
    size: size,
    color: spinnerColor
  }, spinner, {
    center: true
  }))));
}
Button.Input = Input;